/** One shift or absence card, the grammar of plan 0018 part two. */
import type { ApiAbsence, ApiShift } from '@/api/data';
import { T, useT } from '@/i18n';
import { kindOfAbsence, rangeOf } from '@/pages/fravaer/AbsenceRange';
import { type Entry, ruleText } from '@/rules';
import { Menu, Tip, toast } from '@/ui';
import { fmtD, fullName, typeName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { type ReactNode, useState } from 'react';
import cs from './Card.module.css';
import { StatusIcon, type StatusKind } from './StatusIcon';
import { inkShare } from './contrast';
import { absenceMark, cardMarks, punchState, timeText, typeLabel } from './model';
import pcss from './planning/planning.module.css';
import { type TMode, driftMin, driftText, factState, planText } from './timeMode';

// the punch mark's words by punch state; its shape is 'missing' or 'onjob' (plan 0029)
const PT = ['Vakten er over og ingen stemplet', 'Stemplet inn, ikke ut', 'Stemplet inn og ut'];

/**
 * The marks a card draws, in order, each with its shape, its tone and its words. Only what says
 * something is here: a quiet slot is not drawn (plan 0029). The card shows the shapes with the
 * words on hover; the phone's sheet shows the words, because a phone has no hover.
 */
export function cardStatus(s: ApiShift): { key: string; kind: StatusKind; tone: string; text: string }[] {
  const marks = cardMarks(s);
  const out: { key: string; kind: StatusKind; tone: string; text: string }[] = [];
  if (marks.punch !== 'quiet')
    out.push({
      key: 'punch',
      kind: marks.punch === 'bad' ? 'missing' : 'onjob',
      tone: marks.punch,
      text: T(PT[punchState(s)] as string),
    });
  if (marks.approval !== 'quiet')
    out.push({
      key: 'approval',
      kind: marks.approval === 'done' ? 'approved' : 'waiting',
      tone: marks.approval,
      // the state marks are small; each carries its state in words for the screen reader and the tooltip (audit P2)
      text: s.is_approved ? T('Godkjent, timene går til lønn') : T('Ikke godkjent'),
    });
  if (marks.request !== 'quiet')
    out.push({
      key: 'request',
      kind: s.is_swap_requested ? 'swap' : 'offered',
      tone: marks.request,
      text: s.is_swap_requested ? T('Bytte ønsket, venter på deg') : T('Tilbudt av den ansatte'),
    });
  return out;
}

/**
 * The one mark a week card draws (owner, 2026-10-23: "this is not consistent"): the reason someone has to
 * look, else a request, else the proof it is approved. A breach or an unworked past shift is a red dot;
 * a warning alone draws nothing on the card, it is counted on the day, the row and in Saker (handoff
 * 2026-10-24 Q1). The words go to the screen reader and the tooltip.
 */
export function weekMark(
  s: ApiShift,
  entries: Entry[],
  now: number = Date.now(),
): { key: 'rule' | 'punch' | 'request' | 'wait' | 'approval'; text: string } | null {
  const marks = cardMarks(s, now);
  if (entries.some((e) => e.sev === 'd')) return { key: 'rule', text: '' };
  if (marks.punch === 'bad') return { key: 'punch', text: T(PT[0] as string) };
  // a forgotten clock-out on an ended shift is red too, not the green "working now" clock (review 2026-10-28)
  if (forgotOut(s, now)) return { key: 'punch', text: T(PT[1] as string) };
  if (marks.request !== 'quiet')
    return {
      key: 'request',
      text: s.is_swap_requested ? T('Bytte ønsket, venter på deg') : T('Tilbudt av den ansatte'),
    };
  // ended, worked, not approved: a hollow amber ring, so it shows without hover (review 2026-10-28)
  if (marks.approval === 'act') return { key: 'wait', text: T('Ikke godkjent') };
  if (s.is_approved) return { key: 'approval', text: T('Godkjent, timene går til lønn') };
  return null;
}

const tasksText = (n: number) => (n === 1 ? T('1 aktivitet') : T('{n} aktiviteter', { n }));

/** Clocked in, never out, and the shift has ended. */
const forgotOut = (s: ApiShift, now: number = Date.now()): boolean =>
  !!s.punch_in_time && !s.punch_out_time && Date.parse(s.end_time ?? '') < now;

/** Clocked in and out with nothing outstanding: what the card's hover ✓ may approve, the server's own gate. */
export const approvable = (s: ApiShift, entries: Entry[]): boolean =>
  !!(s.punch_in_time && s.punch_out_time) && !s.is_approved && entries.length === 0;

export function ShiftCard({
  s,
  entries,
  onApprove,
  picked,
  onOpen,
  onPick,
  menu,
  compact,
  note,
  v19,
  tasks,
  selected,
  project,
  ruleMark,
  mark,
  scoped,
  tmode,
}: {
  /** the week's Plan | Faktisk switch (#201); left out, the card shows the clocked pair where there is one */
  tmode?: TMode | undefined;
  /** one of the shifts behind the day's or person's count Saker is filtered to (owner 2026-10-25) */
  scoped?: boolean | undefined;
  /** what a shown planning proposal does to this shift (#163): «Ny» or «Endret», dashed */
  mark?: 'new' | 'changed' | undefined;
  /** the rule mark as a button that opens the shift's warnings (issue #146); the icon alone without it */
  ruleMark?: ((icon: ReactNode) => ReactNode) | null | undefined;
  /** the shift's project in the company's word, drawn under the type («Uka», #118) */
  project?: string | undefined;
  s: ApiShift;
  entries: Entry[];
  picked: boolean;
  onOpen: () => void;
  onPick: () => void;
  menu: { key: string; label: ReactNode; onSelect: () => void; danger?: boolean; disabled?: boolean }[];
  compact?: boolean;
  /** what a waiting Hugin proposal would do to this shift, in words ("flyttes", "publiseres") */
  note?: string | undefined;
  /** the V19 card (owner, 2026-10-08): a draft's second line says "Utkast"; the pre-V19 page leaves it out */
  v19?: boolean;
  /** draw the task mark when the shift has tasks (V19 "Vis aktiviteter") */
  tasks?: boolean;
  /** the card whose panel is open (V19: the selected card stays visibly tied to the panel) */
  selected?: boolean;
  /** approve from the card's hover ✓ (handoff 2026-10-24 §4.4); drawn only when the shift is approvable */
  onApprove?: (() => void) | null | undefined;
}) {
  const t = useT();
  const open = s.user_id == null;
  const sev = entries.some((e) => e.sev === 'd') ? 'd' : entries.length ? 'w' : '';
  const color = s.shiftType?.color_code || 'var(--control)';
  const cls = [
    'card',
    !s.published ? 'draft' : '',
    open ? 'open' : '',
    picked ? 'picked' : '',
    scoped ? 'scoped' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const typeName = typeLabel(s.shiftType);
  const title = open ? `${T('Ledig')}, ${typeName ?? ''}` : (typeName ?? T('Ikke planlagt'));
  // V19: a draft says so in words, the tint already says its type (GLOSSARY "Forslag, Utkast, Publisert")
  const shown = v19 && !s.published && !open ? T('Utkast') : title;
  /** Everything the card has to say, most actionable first: a rule, then attendance, request, tasks. */
  const slot: { key: string; tone: string; icon: ReactNode; text: string }[] = [
    ...(entries.length
      ? [
          {
            key: 'rule',
            tone: sev === 'd' ? 'bad' : 'act',
            icon: <Icon name={sev === 'd' ? 'stop' : 'warn'} />,
            text: entries.map((e) => ruleText(t, e)).join(', '),
          },
        ]
      : []),
    // the owner kept every mark (2026-10-08); the ones asking for something come first
    ...cardStatus(s)
      .sort((a, b) => ['bad', 'act', 'done'].indexOf(a.tone) - ['bad', 'act', 'done'].indexOf(b.tone))
      .map((m) => ({
        key: m.key,
        tone: m.tone,
        icon: <StatusIcon kind={m.kind} label={m.text} />,
        text: m.text,
      })),
    ...(tasks && (s.taskAssignments?.length ?? 0) > 0
      ? [
          {
            key: 'tasks',
            tone: 'quiet',
            icon: <Icon name="list" />,
            text: T('{n} aktiviteter', { n: s.taskAssignments?.length ?? 0 }),
          },
        ]
      : []),
  ];
  const one = v19 ? weekMark(s, entries) : null;
  const fact = tmode === 'fakt' ? factState(s) : null;
  const dm = tmode === 'fakt' ? driftMin(s) : null;
  const canOk = !!(v19 && onApprove && approvable(s, entries));
  const [menuOpen, setMenuOpen] = useState(false);
  const taskMark =
    tasks && (s.taskAssignments?.length ?? 0) > 0 ? (
      <Tip text={T('{n} aktiviteter', { n: s.taskAssignments?.length ?? 0 })}>
        <i className="tmark" aria-label={T('{n} aktiviteter', { n: s.taskAssignments?.length ?? 0 })}>
          <Icon name="list" />
        </i>
      </Tip>
    ) : null;
  return (
    <div
      className={`${cls}${v19 ? ` v19c ${cs.card}` : ''}${fact === 'missing' || fact === 'ahead' ? ` ${cs.future}` : ''}${selected ? ' sel' : ''}${mark ? ` ${pcss.marked}` : ''}${canOk ? ` ${cs.canok}` : ''}`}
      aria-current={selected ? 'true' : undefined}
      data-sev={sev}
      data-testid="shift-card"
      data-shift-id={s.id}
      // the card's actions open on right-click; ⋮ is not drawn on a screen with a mouse (owner 2026-10-28)
      onContextMenu={
        v19
          ? (e) => {
              e.preventDefault();
              setMenuOpen(true);
            }
          : undefined
      }
      /* The shift type is the 4px stripe only, in the company's own colour; surface and border
         are the card tokens. The 14% fill of that colour made red types such as Tananger's
         "Day shift" read as errors (plan 0025, Vaktplan, decided 2026-09-21). */
      style={{ '--cl': color, '--clp': `${inkShare(color)}%` } as React.CSSProperties}
      // a shift somebody clocked in on stays where it was worked; trying to drag it says so, instead
      // of nothing happening (owner, 2026-09-27: "drag and drop stopped working", a demo on a Sunday)
      draggable
      data-clocked={s.punch_in_time ? '' : undefined}
      title={s.punch_in_time ? T('Stemplet: blir på dagen og hos personen som jobbet den') : undefined}
      onDragStart={(e) => {
        if (s.punch_in_time) {
          e.preventDefault();
          toast(
            T(
              'Vakten er stemplet og kan ikke flyttes til en annen dag eller person. Åpne vakten for å rette tidene, eller velg «Endre dag likevel».',
            ),
          );
          return;
        }
        e.dataTransfer.setData('text/shift-id', String(s.id));
        e.dataTransfer.effectAllowed = 'move';
        // the card lifts with a slight turn, and its place fades (owner 18.10); the browser takes its
        // picture of the drag image now, so the copy can go on the next frame
        const card = e.currentTarget;
        const ghost = card.cloneNode(true) as HTMLElement;
        const box = card.getBoundingClientRect();
        Object.assign(ghost.style, {
          position: 'fixed',
          top: '-1000px',
          left: '-1000px',
          width: `${box.width}px`,
          transform: 'rotate(3deg)',
          boxShadow: '0 8px 20px rgba(15, 23, 42, 0.18)',
        });
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage?.(ghost, e.clientX - box.left, e.clientY - box.top);
        requestAnimationFrame(() => ghost.remove());
        card.classList.add('dragging');
      }}
      onDragEnd={(e) => {
        e.currentTarget.classList.remove('dragging');
        for (const c of document.querySelectorAll('.cell.drop')) c.classList.remove('drop');
      }}
    >
      <button
        type="button"
        className="hit"
        // Shift-click selects, as a list does; the week card has no select box (owner 2026-10-25)
        onClick={(e) => (v19 && e.shiftKey ? onPick() : onOpen())}
        aria-pressed={selected ? true : undefined}
        aria-label={`${title} ${timeText(s)}${s.user ? `, ${fullName(s.user)}` : ''}${project ? `, ${project}` : ''}, ${s.published ? T('publisert') : T('utkast')}${v19 && slot.length ? `. ${slot.map((m) => m.text).join('. ')}` : ''}`}
      />
      {/* a cell with several shifts draws its cards small, but every card keeps its
          own selection and its own menu: two shifts in one day is ordinary
          (TF-SCHEDULE-COMPLETE acceptance review, finding 4) */}
      {/* the two controls on the card are glyphs with no words; each says on hover what it does,
          through the same Tip the state marks use (owner, 2026-09-20) */}
      <Tip text={T('Velg vakten, for å publisere, godkjenne eller slette flere samtidig')}>
        <button
          type="button"
          className="pick"
          aria-pressed={!!picked}
          onClick={onPick}
          aria-label={`${T('Velg')} ${title} ${timeText(s)}${s.user ? `, ${fullName(s.user)}` : ''}`}
        >
          <Icon name="check" />
        </button>
      </Tip>
      {!v19 && entries.length > 0 && (
        <Tip text={entries.map((e) => ruleText(t, e)).join(', ')}>
          <span className={`cardwarn ${sev}`}>
            <Icon name={sev === 'd' ? 'stop' : 'warn'} />
            {(sev === 'd' || entries.length > 1) && <b>{entries.length}</b>}
          </span>
        </Tip>
      )}
      {/* The time leads on its own line; the status icons and the type share the second, so a
          clocked "09:56–18:04" does not leave the type three letters (plan 0029, measured
          2026-09-24: 16 of 27 type names cut when they shared the time's line). */}
      <div className="ln" style={{ paddingRight: !v19 && entries.length ? 30 : 0 }}>
        {tmode === 'plan' || fact === 'ahead' ? (
          <span className={`tm ${cs.time}`}>{planText(s)}</span>
        ) : fact === 'missing' ? (
          <span className={`tm ${cs.time} ${cs.nopunch}`}>{T('Ikke stemplet')}</span>
        ) : v19 && s.punch_in_time ? (
          <span className={`tm ${cs.time}`}>
            <i className={`${cs.clock}${s.punch_out_time || forgotOut(s) ? '' : ` ${cs.live}`}`}>
              <Icon name="clock" />
            </i>
            {s.punch_out_time || forgotOut(s) ? timeText(s) : timeText(s).replace(/\?$/, '')}
          </span>
        ) : (
          <span className={v19 ? `tm ${cs.time}` : 'tm'}>{timeText(s)}</span>
        )}
      </div>
      {v19 ? (
        // V19 anatomy (handoff 2026-10-08): the time, then the type with one contained status slot.
        // The most actionable mark first, two at most, then "+n"; the panel says every one in words.
        <span className={`v19l2 ${cs.l2}`}>
          <span className={`ttl ${cs.type}`}>{shown}</span>
          {dm != null && (
            <Tip
              text={T(dm > 0 ? '{n} min mer enn planlagt' : '{n} min mindre enn planlagt', {
                n: Math.abs(dm),
              })}
            >
              <span className={`${cs.drift}${dm > 0 ? ` ${cs.up}` : ''}`} data-testid="card-drift">
                {driftText(dm)}
              </span>
            </Tip>
          )}
          <span className={cs.slot} data-testid="card-status">
            {one?.key === 'rule' && ruleMark ? (
              <span className={`${cs.warn} bad`}>{ruleMark(<i className={cs.dot} />)}</span>
            ) : one ? (
              // the mark says its words on hover and to the screen reader (owner 2026-10-28: "unclear what it is for")
              <Tip text={one.text || slot[0]?.text}>
                <i
                  className={
                    one.key === 'rule' || one.key === 'punch'
                      ? 'bad'
                      : one.key === 'request' || one.key === 'wait'
                        ? 'act'
                        : 'done'
                  }
                  aria-label={one.text || slot[0]?.text}
                  data-testid="card-mark"
                >
                  {one.key === 'rule' || one.key === 'punch' ? (
                    <i className={cs.dot} />
                  ) : one.key === 'wait' ? (
                    <i className={`${cs.dot} ${cs.ring}`} />
                  ) : one.key === 'request' ? (
                    <>
                      <StatusIcon kind={s.is_swap_requested ? 'swap' : 'offered'} />
                      <span className={cs.word}>{s.is_swap_requested ? T('Bytte') : T('Tilbudt')}</span>
                    </>
                  ) : (
                    <StatusIcon kind="approved" />
                  )}
                </i>
              </Tip>
            ) : tasks && (s.taskAssignments?.length ?? 0) > 0 ? (
              // the activities on the shift, counted, with its words on hover (owner 2026-10-28: "what is this icon")
              <Tip text={tasksText(s.taskAssignments?.length ?? 0)}>
                <i
                  className="quiet"
                  aria-label={tasksText(s.taskAssignments?.length ?? 0)}
                  data-testid="card-tasks"
                >
                  <Icon name="list" />
                  <span className={cs.word}>{s.taskAssignments?.length ?? 0}</span>
                </i>
              </Tip>
            ) : null}
          </span>
        </span>
      ) : open || compact ? (
        <div className="ttl">
          {shown}
          {taskMark}
        </div>
      ) : (
        <span className="state">
          {/* One rule: colour means somebody has to do something (owner, 2026-09-21). A slot
              with nothing outstanding keeps its position and draws no box, so a clean card is
              title, time and space while a column stays scannable down twenty people. The
              tooltip answers on every slot, the quiet ones included. cardMarks decides, and is
              tested without rendering; see model.ts.

              There is no publishing mark: the diagonal hatch already says which cards are
              drafts and the toolbar already reads "Publiser N utkast". */}
          {/* Only what says something is drawn; a quiet slot is not reserved (plan 0029). The
              words are on hover and for the screen reader. */}
          {cardStatus(s).map((m) => (
            <Tip text={m.text} key={m.key}>
              <i className={m.tone}>
                <StatusIcon kind={m.kind} label={m.text} />
              </i>
            </Tip>
          ))}
          <span className="ttl">{shown}</span>
          {taskMark}
        </span>
      )}
      {project && (
        <span className={`v19pj ${cs.project}`} data-testid="card-project" title={project}>
          {project}
        </span>
      )}
      {note && (
        <span className="pnote" data-testid="proposal-note">
          {note}
        </span>
      )}
      {mark && (
        <span className={pcss.tag} data-testid="plan-mark">
          <Icon name={mark === 'new' ? 'plus' : 'pencil'} />
          {mark === 'new' ? T('Ny') : T('Endret')}
        </span>
      )}
      {canOk && (
        <Tip text={T('Godkjenn timene')}>
          <button
            type="button"
            className={cs.ok}
            data-testid="card-approve"
            onClick={() => onApprove?.()}
            aria-label={`${T('Godkjenn')} ${title} ${timeText(s)}${s.user ? `, ${fullName(s.user)}` : ''}`}
          >
            <Icon name="check" />
          </button>
        </Tip>
      )}
      <Menu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        tip={T('Handlinger for vakten: åpne, publisere, godkjenne, duplisere, slette')}
        trigger={
          <button
            type="button"
            className="kebab"
            aria-label={`${T('Handlinger')} ${title} ${timeText(s)}${s.user ? `, ${fullName(s.user)}` : ''}`}
          >
            <Icon name="more" />
          </button>
        }
        items={menu}
      />
    </div>
  );
}

/**
 * The clock range of a timed absence, '' for a full-day type. The card sits in a day cell, so the
 * start date is not repeated; an end on another day carries its date (TF-CHROME-CORRECTIONS 2).
 * The type is read from the request's own absence_type row, which the admin list includes whole.
 */
export function absenceTime(a: ApiAbsence): string {
  if (kindOfAbsence(a, []) === 'date') return '';
  const r = rangeOf(a);
  if (!r.fromT || !r.toT) return '';
  return r.to === r.from
    ? `${r.fromT}–${r.toT}`
    : T('{from} til {to}', { from: r.fromT, to: `${fmtD(r.to)} ${r.toT}` });
}

export function AbsenceCard({ a, onOpen }: { a: ApiAbsence; onOpen: () => void }) {
  const color = a.absence_type?.color_code || 'var(--slate-l)';
  const name = typeName(a.absence_type);
  const when = absenceTime(a);
  // Sickness is green when approved and red when it is not; everything else keeps the waiting
  // text while it is pending. absenceMark decides; see model.ts.
  const mark = absenceMark(a);
  return (
    <div
      className={`card absence${a.status === 'pending' ? ' pend' : ''}`}
      style={{ '--cl': color, '--cf': 'var(--slate-f)' } as React.CSSProperties}
    >
      <button type="button" className="hit" onClick={onOpen} aria-label={when ? `${name} ${when}` : name} />
      <div className="ln">
        <span className="tm">{when || T('Hele dagen')}</span>
        <span className="ttl">{name}</span>
      </div>
      {mark ? (
        <span className="state">
          <Tip text={T(mark.label)}>
            <i className={mark.tone} aria-label={T(mark.label)} title={T(mark.label)}>
              {mark.glyph}
            </i>
          </Tip>
        </span>
      ) : (
        <span className="venter">{T('Venter på svar')}</span>
      )}
    </div>
  );
}
